import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { ArrowLeft, CheckCircle2, Circle, Headphones, Star } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { useAuth } from "../components/auth/AuthGate";
import { db } from "../firebase/firebase";

const STEPS = [
  "Order Received",
  "Under Review",
  "Quotation Sent",
  "Customer Approved",
  "Assigned",
  "Stitching",
  "Quality Check",
  "Ready for Delivery",
  "Out for Delivery",
  "Delivered",
];
const normalized = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
const stepIndex = (status) => {
  const s = normalized(status);
  if (
    [
      "pending",
      "orderreceived",
      "orderplaced",
      "placed",
      "bookingconfirmed",
      "pickupscheduled",
    ].includes(s)
  )
    return 0;
  if (s === "underreview") return 1;
  if (s === "quotationsent") return 2;
  if (["accepted", "customerapproved", "paymentconfirmed"].includes(s))
    return 3;
  if (["assigned", "tailorassigned"].includes(s)) return 4;
  if (
    [
      "inprogress",
      "stitching",
      "measurementsconfirmed",
      "stitchingstarted",
      "pickupstarted",
      "pickedup",
      "deliveredtotailor",
      "workstarted",
    ].includes(s)
  )
    return 5;
  if (s === "qualitycheck") return 6;
  if (
    [
      "ready",
      "readyfordelivery",
      "readyfordispatch",
      "workcompleted",
      "pickupfromtailor",
    ].includes(s)
  )
    return 7;
  if (s === "outfordelivery") return 8;
  if (s === "delivered") return 9;
  return -1;
};
const show = (value) => String(value || "Not selected").replaceAll("_", " ");
const dateOf = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-IN")
    : "-";
};
function Card({ title, children }) {
  return (
    <section className="card-soft p-6">
      <h2 className="font-display text-2xl">{title}</h2>
      <div className="mt-4 text-sm text-[#5b5a56]">{children}</div>
    </section>
  );
}
function Detail({ label, value }) {
  return (
    <p className="flex justify-between gap-5 py-1.5">
      <strong className="text-[#2B2B2B]">{label}</strong>
      <span className="text-right capitalize">{value}</span>
    </p>
  );
}

export default function OrderDetailsPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!user || !orderId) return;
    return onSnapshot(
      doc(db, "orders", orderId),
      (snapshot) => {
        const value = snapshot.exists()
          ? { id: snapshot.id, ...snapshot.data() }
          : null;
        if (value && value.customerId !== user.uid) {
          setError("You do not have access to this order.");
          setOrder(null);
          return;
        }
        setOrder(value);
        setError(value ? "" : "Order not found.");
      },
      () => setError("Unable to load order details."),
    );
  }, [user, orderId]);
  const current = useMemo(() => stepIndex(order?.status), [order?.status]);
  const submitReview = async () => {
    if (!rating || !notes.trim() || saving) return;
    setSaving(true);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "orders", orderId), {
        customerRating: rating,
        artisanRating: rating,
        notes: notes.trim(),
        updatedAt: serverTimestamp(),
      });
      const reviewRef = doc(collection(db, "customerReviews"));
      batch.set(reviewRef, {
        customerId: user.uid,
        customerName:
          user.displayName || order.customerName || "StichB Customer",
        customerPhotoUrl: user.photoURL || "",
        rating: Number(rating),
        reviewText: notes.trim(),
        itemId: garment.id || order.garmentId || "",
        itemName: garment.name || order.serviceType || "StichB service",
        itemType: order.workflowType || order.orderType || "service",
        orderId: order.orderId || order.id,
        status: "pending",
        showOnHome: false,
        displayOrder: 0,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
    } finally {
      setSaving(false);
    }
  };
  if (loading) return <div className="min-h-screen bg-[#FFF8F2]" />;
  if (!user)
    return (
      <div className="min-h-screen bg-[#FFF8F2]">
        <Navbar />
        <main className="pt-32 text-center">Sign in to view this order.</main>
      </div>
    );
  const garment = order?.garment || {};
  const measurements = order?.measurements || {};
  const fabric = order?.fabric || {};
  const addOns = order?.addOns || {};
  const pickup = order?.pickup || {};
  const delivery = order?.delivery || {};
  const address = delivery.addressSnapshot || {};
  const delivered = normalized(order?.status) === "delivered";
  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar />
      <main className="pt-28 pb-20">
        <div className="max-w-[1000px] mx-auto px-6">
          <button
            onClick={() => navigate("/account/orders")}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524]"
          >
            <ArrowLeft size={16} /> My orders
          </button>
          {error ? (
            <div className="card-soft p-8 mt-8 text-red-600">{error}</div>
          ) : !order ? (
            <div className="card-soft p-8 mt-8">Loading order…</div>
          ) : (
            <>
              <div className="mt-8">
                <div className="font-label text-[#F46524]">Order details</div>
                <h1 className="font-display text-4xl mt-3">
                  {garment.name || order.orderType || "StichB order"}
                </h1>
                <p className="mt-2 text-[#5b5a56]">
                  Order ID: {order.orderId || order.id}
                </p>
                <button
                  onClick={() =>
                    navigate("/profile/support", {
                      state: {
                        startSupport: true,
                        category: "orders",
                        referenceId: order.id,
                        subject: "I need help with this order",
                      },
                    })
                  }
                  className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#D9C7BA] bg-white px-4 py-3 font-semibold text-[#2E5D50]"
                >
                  <Headphones size={18} /> Help with this order
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-5 mt-8">
                <Card title="Summary">
                  <Detail label="Status" value={show(order.status)} />
                  <Detail label="Order date" value={dateOf(order.createdAt)} />
                  <Detail label="Payment" value={show(order.paymentStatus)} />
                  <Detail
                    label="Total"
                    value={`₹${Number(order.total ?? order.pricing?.grandTotal ?? order.basePrice ?? 0).toLocaleString("en-IN")}`}
                  />
                </Card>
                <Card title="Garment / Service">
                  <div className="flex gap-4 items-center">
                    {garment.image ? (
                      <img
                        src={garment.image}
                        alt=""
                        className="w-20 h-20 rounded-xl object-cover"
                      />
                    ) : null}
                    <div>
                      <strong className="text-[#2B2B2B]">
                        {garment.name || order.serviceType}
                      </strong>
                      <p className="mt-1 capitalize">
                        {show(order.workflowType || order.orderType)}
                      </p>
                    </div>
                  </div>
                </Card>
                <Card title="Measurements">
                  <Detail label="Method" value={show(measurements.method)} />
                  {measurements.selectedSize ? (
                    <Detail label="Size" value={measurements.selectedSize} />
                  ) : null}
                  {Object.entries(measurements.manualMeasurements || {}).map(
                    ([key, value]) => (
                      <Detail key={key} label={key} value={`${value} in`} />
                    ),
                  )}
                </Card>
                <Card title="Fabric & add-ons">
                  <Detail label="Fabric" value={show(fabric.option)} />
                  {(addOns.selected || []).map((item) => (
                    <p key={item}>• {item}</p>
                  ))}
                  {addOns.specialInstructions ? (
                    <p className="mt-3">{addOns.specialInstructions}</p>
                  ) : null}
                </Card>
                <Card title="Pickup & delivery">
                  <Detail label="Pickup" value={show(pickup.method)} />
                  <Detail label="Delivery" value={show(delivery.method)} />
                  <Detail label="Date" value={dateOf(delivery.preferredDate)} />
                  <Detail
                    label="Time"
                    value={delivery.preferredTimeSlot || "-"}
                  />
                  <p className="mt-3">
                    {[
                      address.addressLine1,
                      address.city,
                      address.state,
                      address.postalCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </Card>
                <Card title="Timeline">
                  <div className="space-y-3">
                    {STEPS.map((step, index) => {
                      const done = current >= index;
                      return (
                        <div
                          key={step}
                          className={`flex items-center gap-3 ${done ? "text-[#2B2B2B]" : "text-[#a59b94]"}`}
                        >
                          {done ? (
                            <CheckCircle2
                              size={18}
                              className="text-[#F46524]"
                            />
                          ) : (
                            <Circle size={18} />
                          )}
                          <span className={done ? "font-semibold" : ""}>
                            {step}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
              {delivered ? (
                <div className="mt-5">
                  <Card title="Rate your experience">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          disabled={order.customerRating != null}
                          onClick={() => setRating(value)}
                        >
                          <Star
                            className={
                              (order.customerRating || rating) >= value
                                ? "fill-amber-400 text-amber-400"
                                : "text-[#b8ada7]"
                            }
                          />
                        </button>
                      ))}
                    </div>
                    <textarea
                      disabled={order.customerRating != null}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="w-full rounded-xl border border-[#E9D7C8] p-4 mt-4"
                      placeholder="Tell us about your experience"
                    />
                    <p className="mt-2 text-xs text-[#8a8781]">
                      Your review is sent to StitchB for approval before it can
                      appear publicly.
                    </p>
                    <button
                      disabled={
                        !rating ||
                        !notes.trim() ||
                        saving ||
                        order.customerRating != null
                      }
                      onClick={submitReview}
                      className="btn-primary mt-4 disabled:opacity-50"
                    >
                      {order.customerRating != null
                        ? "Review submitted"
                        : saving
                          ? "Submitting…"
                          : "Submit review"}
                    </button>
                  </Card>
                </div>
              ) : null}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
